import { useState, useEffect, useRef, useCallback } from "react";
import { Message, FileAttachment } from "@/types/posit";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { generateMessageId } from "@/utils/message";
import { fileAttachmentToJson } from "@/utils/typeConversions";
import type { User } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";
import { validateFileAttachment } from "@/utils/fileValidation";
import { handleError, ErrorCategory } from "@/utils/errorHandling";
import { messagePersistence } from "@/utils/messagePersistence";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { jsonToFileAttachment } from "@/utils/typeConversions";
import { V1SessionMetadata } from "./promptUtils";

type Posit = Database['public']['Tables']['posits']['Row'];

interface V1ChatAnalysis {
  insights_count: number;
  gaps_count: number;
  topics_identified: number;
  progress_percentage: number;
  progress_level: string;
}

interface V1ChatResponse {
  message: string;
  metadata: V1SessionMetadata;
  analysis: V1ChatAnalysis;
}

interface UseUpdatePositChatV1Return {
  messages: Message[];
  loading: boolean;
  error: string | null;
  content: string;
  setContent: (content: string) => void;
  sendMessage: (message: string, attachments?: FileAttachment[]) => Promise<void>;
  clearMessages: () => void;
  updatePosit: (title: string, description: string, tags: string[]) => Promise<{ id: string } | null>;
  regenerateLastResponse: () => Promise<void>;
  handleQuery: (content: string, attachments?: FileAttachment[]) => Promise<void>;
  loadMessages: () => Promise<void>;
  
  // V1 Enhanced Features
  sessionMetadata: V1SessionMetadata | null;
  chatAnalysis: V1ChatAnalysis | null;
  progressPercentage: number;
  progressLevel: string;
  insightsCount: number;
  knowledgeGapsCount: number;
  domainDetected: string;
  expertiseScore: number;
  
  // Progressive Chat Features
  conversationMode: 'standard' | 'progressive' | 'semantic_search';
  changeConversationMode: (mode: 'standard' | 'progressive' | 'semantic_search') => void;
  cognitiveInsights: any[];
  nextQuestions: string[];
  completionPercentage: number;
  progressiveData: any;
}

export function useUpdatePositChatV1(
  user: User | null,
  isNewPosit: boolean = false,
  existingPositId?: string,
  conversationId?: string | null,
  lazyLoadMessages: boolean = false
): UseUpdatePositChatV1Return {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [positId, setPositId] = useState<string | null>(existingPositId || null);

  // Debug logging
  console.log('🔧 useUpdatePositChatV1 initialized:', {
    userId: user?.id,
    isNewPosit,
    existingPositId,
    conversationId,
    positId,
    lazyLoadMessages
  });
  const [chatSessionId, setChatSessionId] = useState<string | null>(null);
  const [currentChatHistoryId, setCurrentChatHistoryId] = useState<string | null>(null);
  const [content, setContent] = useState<string>("");
  
  // V1 Enhanced State
  const [sessionMetadata, setSessionMetadata] = useState<V1SessionMetadata | null>(null);
  const [chatAnalysis, setChatAnalysis] = useState<V1ChatAnalysis | null>(null);

  // Add progressive chat state - FORCE AI-first processing
  const [conversationMode, setConversationMode] = useState<'standard' | 'progressive' | 'semantic_search'>('progressive');
  const [progressiveData, setProgressiveData] = useState<any>(null);
  const [cognitiveInsights, setCognitiveInsights] = useState<any[]>([]);
  const [nextQuestions, setNextQuestions] = useState<string[]>([]);
  const [completionPercentage, setCompletionPercentage] = useState(0);

  const messagesLoadedRef = useRef(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Enhanced computed values
  const progressPercentage = sessionMetadata?.progress_percentage || 0;
  const progressLevel = sessionMetadata?.progress_level || 'introduction';
  const insightsCount = sessionMetadata?.insights_captured?.length || 0;
  const knowledgeGapsCount = sessionMetadata?.knowledge_gaps?.length || 0;
  const domainDetected = sessionMetadata?.domain || 'general';
  const expertiseScore = sessionMetadata?.expertise_validation_score || 0;

  // Ensure chat history exists (similar to useChatWithPosit.ts)
  const ensureChatHistory = useCallback(async (): Promise<string | null> => {
    if (!user?.id || !positId) {
      return null;
    }

    if (currentChatHistoryId) {
      return currentChatHistoryId;
    }

    // If we have a conversationId from URL, use it
    if (conversationId) {
      setCurrentChatHistoryId(conversationId);
      return conversationId;
    }

    try {
      // Create new chat history record
      const { data, error } = await supabase
        .from('chat_history')
        .insert({
          posit_id: positId,
          user_id: user.id,
          created_at: new Date().toISOString(),
          last_message_at: new Date().toISOString(),
        })
        .select('id')
        .single();

      if (error) {
        throw error;
      }

      setCurrentChatHistoryId(data.id);
      
      // Note: We no longer redirect immediately here. The URL will be updated after message processing.
      
      return data.id;
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to create chat history. Please try again.",
      });
      return null;
    }
  }, [user?.id, positId, conversationId, currentChatHistoryId, navigate, toast]);

  // Load messages from database (only for authenticated users with chat history)
  const { data: messagesData, refetch: refetchMessages } = useQuery({
    queryKey: ["update-posit-messages", positId, currentChatHistoryId || conversationId],
    queryFn: async () => {
      const chatHistoryId = currentChatHistoryId || conversationId;
      if (!positId || !chatHistoryId || !user) return [];
      
      // First, check if the current user has access to this chat history
      const { data: chatHistoryCheck, error: historyError } = await supabase
        .from("chat_history")
        .select("id, user_id")
        .eq("id", chatHistoryId)
        .maybeSingle();
      
      // If no chat history found OR user doesn't own it, redirect to new chat
      if (historyError || !chatHistoryCheck || chatHistoryCheck.user_id !== user.id) {
        const newUrl = `/update-posit/${positId}`;
        navigate(newUrl, { replace: true });
        return [];
      }
      
      const { data, error } = await supabase
        .from("chat_messages")
        .select("*")
        .eq("chat_history_id", chatHistoryId)
        .eq("source", "posit_update") // Only load update messages
        .order("created_at", { ascending: true });
      
      if (error) {
        throw error;
      }
      
      return data.map(msg => ({
        id: msg.id,
        role: msg.role as "user" | "assistant",
        content: msg.content,
        created_at: msg.created_at,
        created_by: msg.created_by,
        attachments: msg.attachments ? msg.attachments.map(jsonToFileAttachment) : [],
      }));
    },
    enabled: !!positId && !!(currentChatHistoryId || conversationId) && !!user,
  });

  // Update messages when data changes
  useEffect(() => {
    if (messagesData && messagesData.length > 0) {
      setMessages(messagesData);
    }
  }, [messagesData]);

  // Initialize chat session
  const initializeChatSession = useCallback(async (): Promise<string | null> => {
    if (!user?.id || !positId || chatSessionId) return chatSessionId;

    try {
      // First ensure we have a chat_history record
      const chatHistoryId = await ensureChatHistory();
      if (!chatHistoryId) {
        return null;
      }

      // Always create new session for fresh update experience (like ChatGPT)
      // Old conversations remain accessible but separate from updates
      const existingSession = null; // Force new session creation
      const selectError = null;

      if (selectError && selectError.code !== 'PGRST116') {
        return null;
      }

      if (existingSession) {
        setChatSessionId(existingSession.id);
        return existingSession.id;
        // Initialize default metadata if not available
        const defaultMetadata: V1SessionMetadata = {
          domain: 'general',
          progress_level: 'introduction',
          progress_percentage: 0,
          topics_covered: [],
          knowledge_gaps: [],
          insights_captured: [],
          next_focus_areas: [],
          expertise_validation_score: 0
        };
        setSessionMetadata(defaultMetadata);
      } else {
        // Create new V1 session
        const { data: newSession, error: insertError } = await supabase
          .from('chat_sessions')
          .insert({
            posit_id: positId,
            created_by: user.id,
            user_id: user.id,
            session_type: 'update_posit_v1',
            metadata: {
              domain: 'general',
              progress_level: 'introduction',
              progress_percentage: 0,
              topics_covered: [],
              knowledge_gaps: [],
              insights_captured: [],
              next_focus_areas: [],
              expertise_validation_score: 0
            }
          })
          .select()
          .single();

        if (insertError) {
          return null;
        }

        if (newSession) {
          setChatSessionId(newSession.id);
          return newSession.id;
          // Initialize default metadata
          const defaultMetadata: V1SessionMetadata = {
            domain: 'general',
            progress_level: 'introduction',
            progress_percentage: 0,
            topics_covered: [],
            knowledge_gaps: [],
            insights_captured: [],
            next_focus_areas: [],
            expertise_validation_score: 0
          };
          setSessionMetadata(defaultMetadata);
        }
      }
    } catch (error) {
      return null;
    }
    
    return null;
  }, [user?.id, positId, chatSessionId, ensureChatHistory]);

  // Load messages with lazy loading support
  const loadMessages = useCallback(async () => {
    // Skip loading old messages for fresh update experience (like ChatGPT)
    // Old conversations remain accessible in conversation history section
    messagesLoadedRef.current = true;
    return;
  }, [chatSessionId]);

  // Effects
  useEffect(() => {
    if (positId && user?.id) {
      initializeChatSession();
    }
  }, [positId, user?.id, initializeChatSession]);

  useEffect(() => {
    if (chatSessionId && !lazyLoadMessages) {
      loadMessages();
    }
  }, [chatSessionId, lazyLoadMessages, loadMessages]);

  // V1 Enhanced send message function
  const sendMessage = useCallback(async (messageText: string, attachments: FileAttachment[] = []) => {
    if (!user?.id || loading) return;

    // Validate attachments
    const validatedAttachments = await Promise.all(
      attachments.map(async (attachment) => {
        const validation = await validateFileAttachment(attachment);
        if (!validation.valid) {
          throw new Error(`Invalid attachment: ${validation.error}`);
        }
        return attachment;
      })
    );

    // Sign URLs for any storage paths
    const attachmentsWithSignedUrls: FileAttachment[] = await Promise.all(
      validatedAttachments.map(async (att) => {
        try {
          if (att.source === 'upload' && att.url && !/^https?:\/\//i.test(att.url)) {
            const { data: signed, error: signError } = await supabase
              .storage
              .from('posit-attachments')
              .createSignedUrl(att.url, 3600);
            if (!signError && signed?.signedUrl) {
              return { ...att, url: signed.signedUrl };
            }
          }
        } catch (_) {}
        return att;
      })
    );

    // If no text provided but we have attachments, add a simple fallback prompt
    const hasAttachments = attachmentsWithSignedUrls.length > 0;
    const finalMessageText = (messageText && messageText.trim().length > 0)
      ? messageText
      : (hasAttachments ? 'Please analyze the attached files.' : '');

    setLoading(true);
    setError(null);

    // Create user message outside try block so it's accessible in catch
    const userMessage: Message = {
      id: generateMessageId(),
      role: 'user',
      content: finalMessageText,
      created_at: new Date().toISOString(),
      created_by: user.id,
      attachments: attachmentsWithSignedUrls.length > 0 ? attachmentsWithSignedUrls : undefined,
      status: 'sending',
    };

    try {
      // Ensure we have a posit and session
      let currentPositId = positId;
      if (!currentPositId && isNewPosit) {
        const { data: newPosit } = await supabase
          .from('posits')
          .insert({
            user_id: user.id,
            title: 'New Posit',
            content: '',
            status: 'draft'
          })
          .select()
          .single();

        if (newPosit) {
          currentPositId = newPosit.id;
          setPositId(currentPositId);
        }
      }

      if (!currentPositId) {
        throw new Error('No posit available');
      }

      // Ensure chat session exists
      let currentSessionId = chatSessionId;
      if (!currentSessionId) {
        currentSessionId = await initializeChatSession();
        if (!currentSessionId) {
          throw new Error('Failed to initialize chat session');
        }
      }

      // Add user message to UI

      setMessages(prev => [...prev, userMessage]);

      // Ensure we have chat history ID
      const chatHistoryId = currentChatHistoryId || await ensureChatHistory();
      if (!chatHistoryId) {
        throw new Error('Failed to get chat history ID for V1 session');
      }

      // Save user message to database (linked to both session and history)
      await supabase
        .from('chat_messages')
        .insert({
          posit_id: currentPositId,
          chat_session_id: currentSessionId,
          chat_history_id: chatHistoryId,
                      role: 'user',
            content: finalMessageText,
            source: 'posit_update',
          attachments: validatedAttachments.length > 0 ? validatedAttachments.map(fileAttachmentToJson) : null,
        });

      // Use clean orchestrator with improved immediate responses
      console.log('🚀 Using clean orchestrator with smart immediate responses');
      console.log('🚀 Parameters being sent:', {
        message: messageText?.substring(0, 100),
        posit_id: currentPositId,
        user_id: user.id,
        session_id: currentSessionId,
        hasPositId: !!currentPositId,
        hasUserId: !!user.id
      });

      const { data: aiResponse, error: aiError } = await supabase.functions.invoke(
        'progressive-update-v4-orchestrator-clean',
        {
          body: {
            message: messageText,
            posit_id: currentPositId,
            user_id: user.id,
            session_id: currentSessionId,
            posit_title: 'Update Conversation'
          }
        }
      );

        if (aiError) throw aiError;
        const response = aiResponse;

        // Handle AI-first orchestrator response format
        console.log('🤖 AI-first response:', response);

        if (response.next_questions) {
          setNextQuestions(response.next_questions);
        }
        if (response.completion_percentage !== undefined) {
          setCompletionPercentage(response.completion_percentage);
        }
        if (response.metadata) {
          setProgressiveData(response.metadata);
        }

        // Extract the main response message
        const assistantContent = response.content || response.message;
        
        // Add assistant message to UI
        const assistantMessage: Message = {
          id: generateMessageId(),
          role: 'assistant',
          content: assistantContent,
          created_at: new Date().toISOString(),
          created_by: user.id,
        };

        setMessages(prev => [...prev, assistantMessage]);

        // Save assistant message to database
        await supabase
          .from('chat_messages')
          .insert({
            posit_id: currentPositId,
            chat_session_id: currentSessionId,
            chat_history_id: chatHistoryId,
            role: 'assistant',
            content: assistantContent,
            source: 'posit_update',
          });

        // Toast removed - AI processing happens silently in background

      // Update chat history last_message_at timestamp
      await supabase
        .from('chat_history')
        .update({ 
          last_message_at: new Date().toISOString() 
        })
        .eq('id', chatHistoryId);

      // Update URL if this was the first message and we created a new chat history
      if (user && chatHistoryId && !conversationId) {
        const newUrl = `/update-posit/${positId}?conversation=${chatHistoryId}`;
        // Add a small delay to ensure database transactions are complete
        setTimeout(() => {
          navigate(newUrl, { replace: true });
        }, 100);
      }
      
      // Mark message as sent and clean up from failed messages
      setMessages(prev => prev.map(msg => 
        msg.id === userMessage.id 
          ? { ...msg, status: 'sent' as const }
          : msg
      ));
      
      // Remove from failed messages localStorage if it was there
      messagePersistence.removeFailedMessage(userMessage.id);

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to send message';
      setError(errorMessage);
      
      handleError(error, 'AI_INTERACTION');

      toast({
        title: "Message Error",
        description: errorMessage,
        variant: "destructive",
      });

      // Mark the user message as failed and save to localStorage instead of removing
      setMessages(prev => prev.map(msg => 
        msg.id === userMessage.id 
          ? { ...msg, status: 'failed' as const, error: errorMessage, retryCount: (msg.retryCount || 0) + 1 }
          : msg
      ));
      
      // Save failed message to localStorage for recovery
      if (positId) {
        messagePersistence.saveFailedMessage(userMessage, positId, errorMessage);
      }
    } finally {
      setLoading(false);
    }
  }, [user?.id, loading, positId, isNewPosit, chatSessionId, initializeChatSession, toast, conversationMode, progressiveData, navigate, conversationId]);

  // Update posit with enhanced content
  const updatePosit = useCallback(async (title: string, description: string, tags: string[]): Promise<{ id: string } | null> => {
    if (!user?.id || !positId) return null;

    try {
      // Collect insights for enhanced content
      const insights = sessionMetadata?.insights_captured || [];
      const topicsCovered = sessionMetadata?.topics_covered || [];
      
      // Enhanced content with metadata
      const enhancedContent = `${description}\n\n--- V1 Enhanced Metadata ---\n` +
        `Domain: ${sessionMetadata?.domain || 'general'}\n` +
        `Progress: ${progressPercentage}% (${progressLevel})\n` +
        `Topics Covered: ${topicsCovered.join(', ')}\n` +
        `Insights: ${insights.length} captured\n` +
        `Expertise Score: ${expertiseScore}/100`;

      const { data, error } = await supabase
        .from('posits')
        .update({
          title,
          content: enhancedContent,
          tags,
          status: 'published',
          updated_at: new Date().toISOString()
        })
        .eq('id', positId)
        .select()
        .single();

      if (error) throw error;

      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: ['posits'] });
      queryClient.invalidateQueries({ queryKey: ['user-posits'] });

      toast({
        title: "Posit Updated Successfully",
        description: `Enhanced with V1 intelligence: ${insights.length} insights, ${progressPercentage}% progress`,
      });

      return { id: data.id };
    } catch (error) {
      handleError(error, 'DATA_PERSISTENCE');

      toast({
        title: "Update Failed",
        description: "Unable to update posit. Please try again.",
        variant: "destructive",
      });

      return null;
    }
  }, [user?.id, positId, sessionMetadata, progressPercentage, progressLevel, expertiseScore, queryClient, toast]);

  // Regenerate last response
  const regenerateLastResponse = useCallback(async () => {
    if (messages.length < 2 || loading) return;

    const lastUserMessage = [...messages].reverse().find(msg => msg.role === 'user');
    if (!lastUserMessage) return;

    // Remove the last assistant message
    setMessages(prev => prev.filter(msg => 
      !(msg.role === 'assistant' && msg.created_at > lastUserMessage.created_at)
    ));

    // Resend the last user message
    await sendMessage(lastUserMessage.content, lastUserMessage.attachments);
  }, [messages, loading, sendMessage]);

  // Clear messages
  const clearMessages = useCallback(() => {
    setMessages([]);
    messagesLoadedRef.current = false;
    
    // Reset V1 metadata
    setSessionMetadata(null);
    setChatAnalysis(null);
  }, []);

  // Add handleQuery function to match V0 interface
      const handleQuery = useCallback(async (messageText: string, attachments: FileAttachment[] = []) => {
      await sendMessage(messageText, attachments);
    }, [sendMessage]);

  // Add method to change conversation mode
  const changeConversationMode = useCallback((mode: 'standard' | 'progressive' | 'semantic_search') => {
    setConversationMode(mode);
  }, []);

  return {
    messages,
    loading,
    error,
    content,
    setContent,
    sendMessage,
    clearMessages,
    updatePosit,
    regenerateLastResponse,
    handleQuery,
    loadMessages,
    
    // V1 Enhanced Properties
    sessionMetadata,
    chatAnalysis,
    progressPercentage,
    progressLevel,
    insightsCount,
    knowledgeGapsCount,
    domainDetected,
    expertiseScore,
    
    // Progressive Chat Properties
    conversationMode,
    changeConversationMode,
    cognitiveInsights,
    nextQuestions,
    completionPercentage,
    progressiveData
  };
} 